import { configureStore as configureReduxStore, combineReducers } from '@reduxjs/toolkit';
import {
  CacheStore, ReducerRegistry, SessionMiddleware, ThunkMiddleware, NotificationsManager,
  TryCatchMiddleware, resourceReducer,
} from '@twnel/web-components';
import dataReducer from './data/reducer';
import { getSessionData } from './data/selectors';
import { isProduction, logException, getVersion } from './data/util';

const [RESOURCES] = Object.keys(resourceReducer);

// Options for RTK's dev-only checks. Large JSON resources (texts, fonts, emoji) make them slow,
// and pending uploads keep their File in the message until it is sent.
export const devChecks = {
  immutableCheck: { ignoredPaths: [RESOURCES] },
  serializableCheck: {
    ignoredPaths: [RESOURCES, /^messages\..+\.file$/],
    ignoredActionPaths: [/^payload\.messages\.\d+\.file$/],
  },
};

export default function configureStore({ storage, context = {} } = {}) {
  // Register reducers.
  const reducerRegistry = ReducerRegistry();
  reducerRegistry.register({ ...resourceReducer, ...dataReducer });
  const storeReducer = combineReducers(reducerRegistry.getReducers());

  // Middleware set up.
  const prefix = getVersion({ prefix: true });
  const session = SessionMiddleware({ prefix, selector: getSessionData, storage });
  const initialState = session.readState(prefix);

  const thunk = ThunkMiddleware({
    reducerRegistry,
    cacheStore: CacheStore(),
    notificationsManager: NotificationsManager(),
    ...context,
  });
  const tryCatch = TryCatchMiddleware((error) => {
    logException(error);
    throw error;
  });

  // Store set up
  const store = configureReduxStore({
    reducer: storeReducer,
    preloadedState: initialState,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({ ...devChecks, thunk: false })
      .prepend(tryCatch, thunk)
      .concat(session.middleware),
    devTools: !isProduction(),
  });
  reducerRegistry.addListener((newReducer) => {
    store.replaceReducer(combineReducers(newReducer));
  });
  return store;
}
